import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  structuredData?: object;
}

const SEOHead = ({
  title = 'BitcoinMind.today - Your AI Gateway to Everything Bitcoin',
  description = 'AI-powered Bitcoin insights, real-time market data, educational guides, and comprehensive analysis. Your complete Bitcoin hub powered by artificial intelligence.',
  keywords = ['bitcoin', 'cryptocurrency', 'AI bitcoin analysis', 'bitcoin news', 'bitcoin price', 'blockchain', 'bitcoin market data', 'on-chain analytics', 'bitcoin education'],
  image = 'https://lovable.dev/opengraph-image-p98pqg.png',
  url = 'https://bitcoinmind.today',
  type = 'website',
  article,
  structuredData,
}: SEOHeadProps) => {
  const fullTitle = title.includes('BitcoinMind') ? title : `${title} | BitcoinMind.today`;
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BitcoinMind.today",
    "description": description,
    "url": "https://bitcoinmind.today",
    "publisher": {
      "@type": "Organization",
      "name": "BitcoinMind",
      "logo": {
        "@type": "ImageObject",
        "url": image
      },
      "sameAs": [
        "https://x.com/bitcoinmindt",
        "https://t.me/bitcoinmindt",
        "https://www.linkedin.com/company/bitcoinmind",
        "https://www.facebook.com/bitcoinmindt/"
      ]
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://bitcoinmind.today/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={url} />

      {/* OpenGraph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="BitcoinMind.today" />

      {/* Article Specific OpenGraph */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bitcoinmindt" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* AI Bot Specific */}
      <meta name="GPTBot" content="index, follow" />
      <meta name="ChatGPT-User" content="index, follow" />
      <meta name="Google-Extended" content="index, follow" />
      <meta name="Claude-Web" content="index, follow" />
      <meta name="PerplexityBot" content="index, follow" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
